import { ExpandMore } from "@material-ui/icons";
import { Box, Typography, Grid, Divider, List, ListItem, 
    ListItemText, Accordion, AccordionSummary, AccordionDetails
} from "@mui/material";
import moment from "moment";

const InvoiceDetails = ({ invoice }) => {
    const formatPeriodDate = (date) => {
        if (typeof date === "string") {
            const momentDate = moment.utc(date);
            if (momentDate.isValid()) {
                return momentDate.format("YYYY-MM-DD HH:mm:ss [UTC]");
            }
        } else if (typeof date === "number") {
            const momentDate = moment.unix(date);
            if (momentDate.isValid()) {
                return momentDate.utc().format("YYYY-MM-DD HH:mm:ss [UTC]");
            }
        }
        return "Invalid Date";
    };

    const renderMetadata = (metadata) => {
        if (!metadata || Object.keys(metadata).length === 0) return <Typography variant="body2" sx={{color:'text.secondary'}}>None</Typography>;
        return (
            <List disablePadding dense>
                {Object.entries(metadata).map(([key, value]) => (
                    <ListItem key={key} disablePadding>
                        <ListItemText secondary={`${key}: ${value}`} />
                    </ListItem>
                ))}
            </List>
        );
    };

    const renderParent = (parent) => {
        if (!parent) return <Typography variant="body2">None</Typography>;
        return (
            <List disablePadding dense>
                <ListItem disablePadding>
                    <ListItemText
                        secondary={
                            <>
                                <Typography variant="body2">Type: {parent.type}</Typography>
                                {parent.subscription_item_details ? (
                                    <>
                                        <Typography variant="body2">Proration: {parent.subscription_item_details.proration ? "True" : "False"}</Typography>
                                        <Typography variant="body2">
                                            Proration Details:{" "}
                                            {parent.subscription_item_details.proration_details?.credited_items
                                                ? JSON.stringify(parent.subscription_item_details.proration_details.credited_items, null, 2)
                                                : "None"}
                                        </Typography>
                                    </>
                                ) : null}
                            </>
                        }
                    />
                </ListItem>
                {parent.invoice_item_details && (
                    <ListItem disablePadding>
                        <ListItemText primary={`Invoice Item Details: ${JSON.stringify(parent.invoice_item_details, null, 2)}`} />
                    </ListItem>
                )}
            </List>
        );
    };

    const renderPricing = (pricing) => {
        if (!pricing) return <Typography variant="body2">None</Typography>;
        return (
            <List disablePadding dense>
                <ListItem disablePadding>
                    <ListItemText
                        secondary={
                            <>
                                <Typography variant="body2">Type: {pricing.type}</Typography>
                                {pricing.unit_amount_decimal && (
                                    <Typography variant="body2">Unit Amount: ${(pricing.unit_amount_decimal / 100).toFixed(2)}</Typography>
                                )}
                                {pricing.price_details?.product && (
                                    <Typography variant="body2">Product: {pricing.price_details.product}</Typography>
                                )}
                            </>
                        }
                    />
                </ListItem>
            </List>
        );
    };

    const renderProrationDetails = (proration_details) => {
        if (!proration_details || !proration_details.credited_items) return <Typography variant="body2" sx={{color:'text.secondary'}}>None</Typography>;
        return (
            <List disablePadding dense>
                <ListItem disablePadding>
                    <ListItemText primary="Credited Items:" />
                </ListItem>
                {proration_details.credited_items.map((item, index) => (
                    <ListItem key={index} disablePadding sx={{ pl: 2 }}>
                        <ListItemText primary={JSON.stringify(item, null, 2)} />
                    </ListItem>
                ))}
            </List>
        );
    };

    const renderTaxAmounts = (tax_amounts) => {
        if (!tax_amounts || tax_amounts.length === 0) return <Typography variant="body2">-</Typography>;
        return (
            <Box sx={{ width: "100%" }}>
                {tax_amounts.map((tax, index) => (
                    <Accordion key={index}
                        sx={{ mb: 1, boxShadow: "none", border: "1px solid #e0e0e0", "&:before": { display: "none" }, width: "100%" }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}
                            sx={{ backgroundColor: "#f5f5f5", minHeight: "48px", "& .MuiAccordionSummary-content": { margin: "8px 0" } }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>${(tax.amount / 100).toFixed(2)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, backgroundColor: "#fafafa", color: "text.secondary" }}>
                            <Box>
                                <Typography variant="body2">Tax Rate: {tax.tax_rate}</Typography>
                                <Typography variant="body2">Reason: {tax.taxability_reason || "None"}</Typography>
                                <Typography variant="body2">Taxable Amount: ${(tax.taxable_amount / 100).toFixed(2)}</Typography>
                                <Typography variant="body2">Inclusive: {tax.inclusive ? "True" : "False"}</Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    const renderTaxes = (taxes) => {
        if (!taxes || taxes.length === 0) return <Typography variant="body2">None</Typography>;
        return (
            <Box sx={{ width: "100%" }}>
                {taxes.map((tax, index) => (
                    <Accordion key={index}
                        sx={{ mb: 1, boxShadow: "none", border: "1px solid #e0e0e0", "&:before": { display: "none" }, width: "100%" }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}
                            sx={{ backgroundColor: "#f5f5f5", minHeight: "48px", "& .MuiAccordionSummary-content": { margin: "8px 0" } }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: "bold" }}>${(tax.amount / 100).toFixed(2)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, backgroundColor: "#fafafa", color: "text.secondary" }}>
                            <Box>
                                <Typography variant="body2">Behavior: {tax.tax_behavior}</Typography>
                                <Typography variant="body2">Tax Rate: {tax.tax_rate_details?.tax_rate}</Typography>
                                <Typography variant="body2">Reason: {tax.taxability_reason}</Typography>
                                <Typography variant="body2">Type: {tax.type}</Typography>
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    if (!invoice) {
        return (
            <Box sx={{ p: 2, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body1" color="text.secondary">
                    Search for a customer and then select an invoice to view its details here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2, height: "100%", overflowY: "auto", backgroundColor: "#fff", borderLeft: "1px solid #e0e0e0" }}>
            <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>Invoice Details: {invoice.number}</Typography>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Invoice Number:</Typography>
                    <Typography>{invoice.number}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Created:</Typography>
                    <Typography>{moment.unix(invoice.created).format("YYYY-MM-DD HH:mm:ss [UTC]")}</Typography>
                </Grid>
                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Line Items:</Typography>
                    {invoice.lines?.data?.map((line, index) => (
                        <Accordion key={index} sx={{ mb: 2, boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)" }}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>{line.description}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        ${((line.amount || 0) / 100).toFixed(2)} ({line.type || "unknown"})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Grid container spacing={1}>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Amount:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>${((line.amount || 0) / 100).toFixed(2)}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Amount Excluding Tax:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            ${((line.amount_excluding_tax || 0) / 100).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Currency:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>{(line.currency).toUpperCase()}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Description:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>{line.description || "None"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Discount Amounts:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            {line.discount_amounts?.length > 0
                                                ? line.discount_amounts.map((d) => `$${(d.amount / 100).toFixed(2)}`).join(", ")
                                                : "None"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Discounts:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            {line.discounts?.length > 0 ? line.discounts.join(", ") : "None"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Metadata:</Typography>
                                        {renderMetadata(line.metadata)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Parent:</Typography>
                                        {renderParent(line.parent)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Period:</Typography>
                                        <List disablePadding>
                                            <ListItem disablePadding>
                                                <ListItemText secondary={
                                                    <>
                                                        <Typography variant="body2">Start: {formatPeriodDate(line.period?.start)}</Typography>
                                                        <Typography variant="body2">End: {formatPeriodDate(line.period?.end)}</Typography>
                                                    </>
                                                } />
                                            </ListItem>
                                        </List>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Pretax Credit Amounts:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            {line.pretax_credit_amounts?.length > 0
                                                ? line.pretax_credit_amounts.map((c) => `$${(c.amount / 100).toFixed(2)}`).join(", ")
                                                : "None"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Pricing:</Typography>
                                        {renderPricing(line.pricing)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Proration:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>{line.proration ? "True" : "False"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Proration Details:</Typography>
                                        {renderProrationDetails(line.proration_details)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Quantity:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>{line.quantity || "None"}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Tax Amounts:</Typography>
                                        {renderTaxAmounts(line.tax_amounts)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Tax Rates:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            {line.tax_rates?.length > 0 ? line.tax_rates.join(", ") : "None"}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Taxes:</Typography>
                                        {renderTaxes(line.taxes)}
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Type:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>{line.type}</Typography>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>Unit Amount Excluding Tax:</Typography>
                                        <Typography variant="body2" sx={{color:'text.secondary'}}>
                                            ${((line.unit_amount_excluding_tax || 0) / 100).toFixed(2)}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    <Divider sx={{ my: 2 }} />
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Subtotal:</Typography>
                        <Typography variant="subtitle1">
                            ${(invoice.subtotal ? invoice.subtotal / 100 : 0).toFixed(2)}
                        </Typography>
                    </div>
                    <Divider sx={{ my: "5px" }} />
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Tax:</Typography>
                        <Typography variant="subtitle1">
                            ${(invoice.tax ? invoice.tax / 100 : 0).toFixed(2)}
                        </Typography>
                    </div>
                    <Divider sx={{ my: "5px" }} />
                    <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>Total:</Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                            ${(invoice.total ? invoice.total / 100 : 0).toFixed(2)}
                        </Typography>
                    </div>
                </Grid>
            </Grid>
        </Box>
    );
};

export default InvoiceDetails;